import {
	CloudServerOutlined,
	CreditCardOutlined,
	ExperimentOutlined,
	FileSearchOutlined,
	PlayCircleOutlined,
	TagsOutlined,
	TeamOutlined
} from '@ant-design/icons';
import { CartIcon, DownloadIcon, HomeIcon } from 'assets/images/svg/Menu';
import { ZezamiiIcon } from 'zComponents/atoms/ZezamiiIcon';
import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// LAZY LOAD OF Public PAGES IN THE PLATFORM WITH THEIR OWN NAME OF CHUNK
const QrSignup = lazy(() => import('publicContainer/QrSignup'));
const Signin = lazy(() => import('publicContainer/Signin'));
const CreatePassword = lazy(() => import('publicContainer/CreatePassword'));
const ForgotPassword = lazy(() => import('publicContainer/ForgotPassword'));
// Kinde redirect handler (C-2). Public route — the user is mid-login and has no session yet.
const Callback = lazy(() => import('./Callback'));
// Kinde onboarding bridge landing (admin-invite). Public route — the invitee has no session yet.
const Welcome = lazy(() => import('publicContainer/Welcome'));
const ApprovalDecision = lazy(() => import('publicContainer/ApprovalDecision'));
const VisitInvite = lazy(() => import('publicContainer/VisitLink'));
const VisitCheckOut = lazy(() => import('publicContainer/VisitLink').then((m) => ({ default: m.VisitCheckOut })));
const VisitWalkUp = lazy(() => import('publicContainer/VisitWalkUp'));
const BillingSetup = lazy(() => import('publicContainer/BillingSetup'));
const BillingSetupReturned = lazy(() =>
	import('publicContainer/BillingSetup/BillingSetupReturn').then((m) => ({ default: m.BillingSetupReturned }))
);
const BillingSetupCancelled = lazy(() =>
	import('publicContainer/BillingSetup/BillingSetupReturn').then((m) => ({ default: m.BillingSetupCancelled }))
);

const FourZeroFour = lazy(() => import('publicContainer/404'));
const FourZeroOne = lazy(() => import('publicContainer/401'));
const FiveHundred = lazy(() => import('publicContainer/500'));

// ?  LAZY LOAD OF PRIVATE PAGES IN THE PLATFORM WITH THEIR OWN NAME OF CHUNK
const DashboardChooser = lazy(() => import('publicContainer/DashboardChooser'));
const Home = lazy(() => import('mainDashboard/Home'));
const Sites = lazy(() => import('mainDashboard/Sites'));
const Spaces = lazy(() => import('mainDashboard/Spaces'));
const FloorPlan = lazy(() => import('mainDashboard/FloorPlan'));
const Inventory = lazy(() => import('mainDashboard/Inventory'));
const Demo = lazy(() => import('mainDashboard/Demo'));
const FeatureFlags = lazy(() => import('mainDashboard/FeatureFlags'));
// const Organisation = lazy(() => import('mainDashboard/Organisations'));

const Settings = lazy(() => import('mainDashboard/Settings'));
const AccessSetup = lazy(() => import('mainDashboard/AccessSetup'));
const People = lazy(() => import('mainDashboard/People'));
const Teams = lazy(() => import('mainDashboard/Teams'));
const Credentials = lazy(() => import('mainDashboard/Credentials'));
const Devices = lazy(() => import('mainDashboard/Devices'));
const Cameras = lazy(() => import('mainDashboard/Cameras'));
const DeviceGroups = lazy(() => import('mainDashboard/DeviceGroups'));
const DeviceCategories = lazy(() => import('mainDashboard/DeviceCategories'));
const Permissions = lazy(() => import('mainDashboard/Permissions'));
const OpenAccess = lazy(() => import('mainDashboard/OpenAccess'));
const Bookings = lazy(() => import('mainDashboard/Bookings'));
const Visitors = lazy(() => import('mainDashboard/Visitors'));
const RoomStays = lazy(() => import('mainDashboard/RoomStays/RoomStaysPage'));
const Roles = lazy(() => import('mainDashboard/Roles'));
const Partner = lazy(() => import('mainDashboard/Partner'));
const PartnerPortalRedirect = lazy(() => import('mainDashboard/PartnerPortal'));
const PartnerAdmin = lazy(() => import('mainDashboard/PartnerAdmin'));
const AlertHub = lazy(() => import('mainDashboard/AlertHub'));
const DataGridAcceptance = lazy(() => import('mainDashboard/DataGridAcceptance'));
const AuditLog = lazy(() => import('mainDashboard/AuditLog'));
const Approvals = lazy(() => import('mainDashboard/Approvals'));
const Billing = lazy(() => import('mainDashboard/Billing'));

//Locker Dashboard routes
const PeopleLocker = lazy(() => import('lockerDashboard/People'));
const HomeLocker = lazy(() => import('lockerDashboard/Home'));
const LockersLocker = lazy(() => import('lockerDashboard/Lockers'));
const SettingsLocker = lazy(() => import('lockerDashboard/Settings'));
const DevicesLocker = lazy(() => import('lockerDashboard/Devices'));

export const PUBLIC_ROUTES = [
	{
		key: 'qrSignup',
		path: '/qrsignup',
		exact: false,
		component: QrSignup
	},
	{
		path: '',
		exact: true,
		component: Signin
	},
	{
		path: 'signin',
		component: Signin
	},
	{
		path: 'forgotpassword',
		component: ForgotPassword
	},

	{
		path: 'createPassword',
		component: CreatePassword
	},
	{
		path: 'callback',
		component: Callback
	},
	{
		path: 'welcome',
		component: Welcome
	},
	{
		// 🛑 PUBLIC BY DESIGN: approvers decide from email; the signed token is the credential, verified server-side.
		// ⚠️ Path is baked into sent emails (App Server `decisionLinkUrl`) — renaming breaks links in flight.
		path: 'approve/:token',
		component: ApprovalDecision
	},
	{
		// 🛑 Paths baked into sent invite/sign-out emails (the App Server builds both) — never rename. Public: the
		// signed token is the credential, verified server-side; pages only read on load, actions are POSTs.
		path: 'vms/i/:token',
		component: VisitInvite
	},
	{
		path: 'vms/co/:token',
		component: VisitCheckOut
	},
	{
		// 🛑 Printed on lobby QR codes — this path can never change. No token by design: slugs are public and the
		// server only lets anyone register an arrival; it answers one uniform 404 for anything unavailable.
		path: 'vms/w/:orgSlug/:siteSlug',
		component: VisitWalkUp
	},
	{
		// 🛑 PUBLIC BY DESIGN: a site-trial customer's billing contact adds a card from an emailed link; the token is
		// the credential, verified server-side (App Server /v5/public/billing). ⚠️ `billing-setup/:token` is baked into
		// sent emails (App Server `cardSetupPageUrl`) and `return` / `cancelled` into Stripe sessions — never rename.
		// The page strips the token to `billing-setup` (kept in this tab only), which is why that path exists too.
		path: 'billing-setup/return',
		component: BillingSetupReturned
	},
	{
		path: 'billing-setup/cancelled',
		component: BillingSetupCancelled
	},
	{
		path: 'billing-setup/:token',
		component: BillingSetup
	},
	{
		path: 'billing-setup',
		component: BillingSetup
	},

	{
		path: '404',
		component: FourZeroFour
	},
	{
		path: '401',
		component: FourZeroOne
	},
	{
		path: '500',
		component: FiveHundred
	}
];

// ? `sidebar.position`: 0 = top nav, 1 = bottom nav; `sidebar.access` gates which roles see the option.
// ? Setting sidebar to false hides the option from the sidebar.
export const MAIN_DASHBOARD_ROUTES = [
	{
		path: '', //? The path of the base dashboard route.
		component: Home,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Dashboard',
		description: 'Your estate at a glance — what needs attention across all sites.',
		leftIcon: <HomeIcon />,
		entityTypeID: 1,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Dashboard',
			position: 0
		}
	},
	{
		path: 'people/:tab?',
		component: People,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'People',
		description: "Know who's in your organisation — people, roles, and mobile access status.",
		leftIcon: <ZezamiiIcon name='user' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'People',
			position: 0
		}
	},
	{
		path: 'teams',
		component: Teams,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Teams',
		description: 'Manage teams and see who belongs where.',
		leftIcon: <ZezamiiIcon name='users' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Teams',
			position: 0
		}
	},
	{
		path: 'credentials/:tab?',
		component: Credentials,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Credentials',
		description: 'Zezamii Pass, cards, and PIN codes assigned to your people.',
		leftIcon: <ZezamiiIcon name='credential' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Credentials',
			position: 0
		}
	},
	{
		path: 'permissions/:tab?',
		component: Permissions,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Access',
		description: 'View and manage all access grants across people, teams, devices, and groups.',
		leftIcon: <ZezamiiIcon name='product-access' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Access',
			position: 0
		}
	},
	{
		path: 'open-access',
		component: OpenAccess,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Open Access',
		description: 'Weekly schedules that hold locks open and limit when people can enter.',
		leftIcon: <ZezamiiIcon name='calendar' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Open Access',
			position: 0
		}
	},
	{
		path: 'alerts',
		component: AlertHub,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Alerts & Operations',
		description: 'Notifications, alerts, and operational triage — in one place.',
		leftIcon: <ZezamiiIcon name='notification' />,
		// Dark-launch gate: the Sidebar hides this item until the `notifications` flag is on.
		featureFlag: 'notifications',
		// Sidebar shows it to Developers only for now; the URL stays reachable for every role above.
		developerOnly: true,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Alerts & Operations',
			position: 1
		}
	},
	{
		path: 'visitors/:tab?',
		component: Visitors,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		// 🛑 NO route-level `featureFlag` here. The page is TWO products: the Access-owned visitor Directory
		// (Cloud Keys; works for every org) and the VMS sections. The page gates the VMS sections itself
		// (Visitors/index.jsx SECTIONS + selectHasVmsModule) and loads nothing VMS-side without them, so a
		// route gate only hid the Directory from every org without VMS (it was in the nav before 2026-09-04).
		name: 'Visitors',
		description: 'Manage temporary access credentials for guests and contractors.',
		leftIcon: <ZezamiiIcon name='visitor' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Visitors',
			position: 0
		}
	},
	{
		// Room cards setup (room-cards-setup PLAN.md §5.2): Setup, Card Stock and Rooms per site. Dark behind
		// credentials.roomStays: the Sidebar hides it until the flag resolves ON, and the page itself renders nothing
		// and sends nothing while it is off (INV-01) — routeRenderer does not read `featureFlag`, so the page must.
		// Grouped under Access in Sidebar/navGroups.js: an ungrouped route never shows in the nav.
		path: 'room-stays/:tab?',
		component: RoomStays,
		exact: true,
		access: [201, 101, 103],
		featureFlag: 'credentials.roomStays',
		name: 'Front Desk',
		description: 'Set up a site for floating room cards, enrol base cards and run the rooms board.',
		leftIcon: <ZezamiiIcon name='schedule' />,
		sidebar: {
			access: [201, 101, 103],
			name: 'Front Desk',
			position: 0
		}
	},
	{
		path: 'devices/:tab?',
		component: Devices,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Devices',
		description: 'All locks, readers, and access points across your spaces.',
		leftIcon: <ZezamiiIcon name='lock' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Devices',
			position: 0
		}
	},
	{
		path: 'cameras/:tab?',
		component: Cameras,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Cameras',
		description: 'Live view of the cameras across your spaces.',
		leftIcon: <ZezamiiIcon name='camera' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Cameras',
			position: 0
		}
	},
	{
		path: 'device-groups/:tab?',
		component: DeviceGroups,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Access Groups',
		description: 'Grouped device sets for streamlined access management.',
		leftIcon: <ZezamiiIcon name='space' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Access Groups',
			position: 0
		}
	},
	{
		path: 'device-categories',
		component: DeviceCategories,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Device Categories',
		description: 'Organise devices by type for filtering and reporting.',
		leftIcon: <TagsOutlined />,
		// Not a top-level nav item — lives under Settings › Workspace.
		sidebar: false
	},
	{
		path: 'sites',
		component: Sites,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Sites',
		description: 'Sites, buildings, and floors that make up your physical spaces.',
		leftIcon: <ZezamiiIcon name='building' />,
		entityTypeID: 2,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Sites',
			position: 0
		}
	},
	{
		path: 'spaces',
		component: Spaces,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Spaces',
		description: 'Manage sites, spaces, and devices across your organisation.',
		leftIcon: <ZezamiiIcon name='space' />,
		entityTypeID: 2,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Spaces',
			position: 0
		}
	},
	{
		path: 'floorplan',
		component: FloorPlan,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Floorplans',
		leftIcon: <ZezamiiIcon name='floor' />,
		entityTypeID: 2,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Floorplans',
			position: 0
		}
	},
	{
		path: 'roles',
		component: Roles,
		exact: true,
		access: [201, 101, 111],
		name: 'Roles',
		description: 'Manage admin roles and permissions across the organisation.',
		leftIcon: <ZezamiiIcon name='shield-check' />,
		sidebar: {
			access: [201, 101, 111],
			name: 'Roles',
			position: 0
		}
	},
	{
		// Installer portal: off the sidebar (2026-10-28), still reachable by URL; the container self-gates.
		path: 'partner',
		component: Partner,
		exact: true,
		access: [201, 101, 111],
		name: 'Installer Portal',
		description: 'Scoped device access, partner resources, and grant expiry for installers.',
		leftIcon: <ZezamiiIcon name='shield-check' />,
		featureFlag: 'partnerPortal'
	},
	{
		path: 'settings',
		component: Settings,
		access: [201, 202, 101, 111, 102, 103, 108, 109],
		name: 'Settings',
		description: 'Organisation settings, integrations, and administrator access.',
		leftIcon: <ZezamiiIcon name='settings' />,
		entityTypeID: 10,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 108, 109],
			name: 'Settings',
			position: 0
		}
	},
	{
		key: 'approvals',
		// 🛑 FOUR gates must agree: this `access`, the sidebar's, the server's WRITE_ADMIN_ROLE_IDS minus 108/109
		// (read-only), AND a group in Sidebar/navGroups.js — ungrouped routes silently vanish from the nav.
		path: 'approvals',
		component: Approvals,
		exact: true,
		access: [101, 102, 111, 201, 103, 107, 110],
		name: 'Approvals',
		description: 'Review and decide access requests waiting on you.',
		leftIcon: <FileSearchOutlined />,
		// Sidebar shows it to Developers only for now; the URL stays reachable for every role above.
		developerOnly: true,
		sidebar: {
			access: [101, 102, 111, 201, 103, 107, 110],
			name: 'Approvals',
			position: 0
		}
	},
	{
		// Billing (site-trial plan D48): where a customer's billing contact signs in and saves a card. 🛑 FIVE gates:
		// this `access`, `sidebar.access` (identical), the `billing` flag on the nav item (as Settings > Billing),
		// the 'Admin' group in Sidebar/navGroups.js, and the SERVER — which alone decides who may save the card (the
		// named billing contact) and refuses everyone else. `billingContactAccess` lets a billing-only session
		// (`billingContactOnly` on its org, no roles) reach this route and nothing else (routeRenderer). ⚠️ The path is
		// baked into the server's Stripe return URLs and the email button (`/access/billing?orgID=`): never rename it.
		path: 'billing',
		component: Billing,
		exact: true,
		access: [101, 102, 115],
		billingContactAccess: true,
		name: 'Billing',
		description: 'Review your licence and save a payment card.',
		leftIcon: <CreditCardOutlined />,
		featureFlag: 'billing',
		sidebar: {
			access: [101, 102, 115],
			name: 'Billing',
			position: 0
		}
	},
	{
		// ⚠️ Deliberately narrower than the API: it names who did what, so only 101 and 201. Three gates — this
		// array, sidebar `adminOnly`, the page's role check — move together; AuditLog/index.test.jsx locks them.
		path: 'audit-log',
		component: AuditLog,
		exact: true,
		access: [101, 201],
		name: 'Audit Log',
		description: 'Search and review administrator activity across the organisation.',
		leftIcon: <FileSearchOutlined />,
		adminOnly: true,
		sidebar: {
			access: [101, 201],
			name: 'Audit Log',
			position: 0
		}
	},
	{
		// Demo admin: developerOnly + [201], endpoints gated by developerCheck. 🛑 Never gate on 101 — the demo
		// login holds 101, so a prospect would see the button that wipes their own workspace.
		path: 'demo',
		component: Demo,
		exact: true,
		access: [201],
		name: 'Demo',
		leftIcon: <PlayCircleOutlined />,
		developerOnly: true,
		sidebar: {
			access: [201],
			name: 'Demo',
			position: 0
		}
	},
	{
		path: 'feature-flags',
		component: FeatureFlags,
		exact: true,
		access: [201],
		name: 'Feature Flags',
		leftIcon: <ExperimentOutlined />,
		developerOnly: true,
		sidebar: {
			access: [201],
			name: 'Feature Flags',
			position: 0
		}
	},
	{
		path: 'partner-admin',
		component: PartnerAdmin,
		exact: true,
		access: [201],
		name: 'Partner Admin',
		leftIcon: <TeamOutlined />,
		developerOnly: true,
		sidebar: {
			access: [201],
			name: 'Partner Admin',
			position: 0
		}
	},
	{
		path: 'inventory',
		component: Inventory,
		exact: true,
		access: [201, 203],
		name: 'Inventory',
		leftIcon: <CloudServerOutlined />, //? Set the icon react component here
		entityTypeID: 13,
		developerOnly: true,
		sidebar: {
			access: [201, 203],
			name: 'Inventory',
			position: 0
		}
	},
	{
		path: 'onboard',
		component: AccessSetup,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Setup',
		sidebar: false
	},
	{
		path: 'datagrid-acceptance',
		component: DataGridAcceptance,
		exact: true,
		sidebar: false,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109]
	},
	{
		path: 'buy-locks', // A unique key to identify the item
		name: 'Buy Locks',
		access: [],
		leftIcon: <CartIcon />,
		sidebar: false
	},
	{
		path: 'download-app', // A unique key to identify the item
		name: 'Download App',
		access: [],
		leftIcon: <DownloadIcon />,
		sidebar: {
			position: 1,
			name: 'Download Zezamii Access'
		}
	}
];

export const LOCKER_DASHBOARD_ROUTES = [
	{
		path: '', //? The path of the base dashboard route.
		component: HomeLocker,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Home',
		leftIcon: <HomeIcon />,
		entityTypeID: 1,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Home',
			position: 0
		}
	},
	{
		path: 'lockers',
		component: LockersLocker,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Lockers',
		leftIcon: <ZezamiiIcon name='locker-unit' />,
		entityTypeID: 2,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Lockers',
			position: 0
		}
	},
	{
		// Focused Devices/Gateways view under Lockers (Spaces group). Reuses the Access Devices page
		// in its `lockers` variant — org-wide devices, Access-product columns hidden.
		path: 'devices/:tab?',
		component: DevicesLocker,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Devices',
		leftIcon: <ZezamiiIcon name='lock' />,
		entityTypeID: 2,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Devices',
			position: 0
		}
	},
	{
		path: 'people/:tab?',
		component: PeopleLocker,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'People',
		leftIcon: <ZezamiiIcon name='user' />,
		entityTypeID: 2,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'People',
			position: 0
		}
	},
	{
		path: 'settings',
		component: SettingsLocker,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Settings',
		leftIcon: <ZezamiiIcon name='settings' />,
		entityTypeID: 2,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Settings',
			position: 0
		}
	},
	{
		path: 'download-app', // A unique key to identify the item
		name: 'Download App',
		access: [],
		leftIcon: <DownloadIcon />,
		sidebar: {
			position: 1,
			name: 'Download Zezamii Lockers'
		}
	}
];
// Bookings product on the Access shell; the Sidebar self-selects this nav when the product is 'bookings'.
// Shared nav paths reuse Access containers under /bookings/<path>; 'floorplan' is a hidden support route.
const BOOKINGS_SHARED_NAV_PATHS = ['spaces', 'devices', 'cameras', 'teams', 'visitors', 'roles', 'settings'];
const BOOKINGS_SHARED_NAV_ROUTES = MAIN_DASHBOARD_ROUTES.filter((r) =>
	BOOKINGS_SHARED_NAV_PATHS.includes(r.path.split('/')[0])
).map((r) => {
	// Access 'spaces' and Bookings 'sites' both label as "Spaces": drop the inherited sidebar slot but keep
	// it routable, since drilling into a space resolves through it.
	if (r.path.split('/')[0] !== 'spaces') return r;
	const { sidebar: _hidden, ...withoutSidebar } = r;
	return withoutSidebar;
});

export const BOOKINGS_DASHBOARD_ROUTES = [
	// The operator surface is split into pages (one per former tab); all mount the same Bookings
	// container, which picks the view from the URL segment. Resources is the index ('').
	{
		path: '', //? Index — Overview (insights dashboard: stats, trends, popular resources).
		component: Bookings,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Overview',
		description: 'Booking activity at a glance — stats, trends and popular resources.',
		leftIcon: <ZezamiiIcon name='activity' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Overview',
			position: 0
		}
	},
	{
		path: 'resources',
		component: Bookings,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Resources',
		description: 'Bookable rooms & spaces and their booking configuration.',
		leftIcon: <ZezamiiIcon name='space' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Resources',
			position: 0
		}
	},
	{
		path: 'list',
		component: Bookings,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Bookings',
		description: 'Reserve a resource for a time window — bookings, calendar, approvals & log.',
		leftIcon: <ZezamiiIcon name='schedule' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Bookings',
			position: 0
		}
	},
	// Legacy deep links from before Bookings became one tabbed page: routable, no `sidebar`; keep for bookmarks.
	{
		path: 'approvals',
		component: Bookings,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Approvals',
		description: 'Booking requests awaiting approval.',
		leftIcon: <ZezamiiIcon name='granted' />
	},
	{
		path: 'calendar',
		component: Bookings,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Calendar',
		description: 'Booking calendar and availability.',
		leftIcon: <ZezamiiIcon name='calendar' />
	},
	{
		path: 'log',
		component: Bookings,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Booking Log',
		description: 'Audit trail of booking activity, with CSV export.',
		leftIcon: <ZezamiiIcon name='notification' />
	},
	{
		path: 'day-passes',
		component: Bookings,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Entry Passes',
		description: 'Guest entry for a validity period — pass types, capacity, sales & QR links.',
		leftIcon: <ZezamiiIcon name='product-pass' />,
		// Dark-launch gate: the Sidebar hides this item and the Bookings container
		// deep-link-falls-back to Resources until the `day-pass` flag is on per-org.
		featureFlag: 'day-pass',
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Entry Passes',
			position: 0
		}
	},
	{
		path: 'sites',
		component: Sites,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Spaces',
		description: 'Sites, buildings, and floors that hold your bookable spaces.',
		leftIcon: <ZezamiiIcon name='building' />,
		entityTypeID: 2,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'Spaces',
			position: 0
		}
	},
	{
		path: 'people/:tab?',
		component: People,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'People',
		description: "Know who's in your organisation — people, roles, and mobile access status.",
		leftIcon: <ZezamiiIcon name='user' />,
		sidebar: {
			access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
			name: 'People',
			position: 0
		}
	},
	{
		path: 'floorplan',
		component: FloorPlan,
		exact: true,
		access: [201, 202, 101, 111, 102, 103, 104, 105, 2, 107, 108, 109],
		name: 'Floorplans',
		leftIcon: <ZezamiiIcon name='floor' />,
		entityTypeID: 2,
		sidebar: false // support route.
	},
	// Universal Spaces/People sub-items (Devices, Cameras, Teams, Visitors, Roles), reused from Access.
	...BOOKINGS_SHARED_NAV_ROUTES
];

// Vision product: Cameras page plus shared Spaces/People nav on the Access shell. Cameras stays at
// 'cameras/:tab?' so useTabRoute resolves; the index ('') redirects there.
const VISION_ACCESS = [201, 202, 101, 102, 103, 104, 105, 2, 107, 108, 109];
const VISION_SHARED_NAV_PATHS = ['sites', 'spaces', 'devices', 'people', 'teams', 'visitors', 'roles', 'settings'];
const VISION_SHARED_NAV_ROUTES = MAIN_DASHBOARD_ROUTES.filter((r) =>
	VISION_SHARED_NAV_PATHS.includes(r.path.split('/')[0])
);

const VisionIndexRedirect = () => <Navigate to='cameras' replace />;

export const VISION_DASHBOARD_ROUTES = [
	{
		path: '', //? Index — redirect to the Cameras surface.
		component: VisionIndexRedirect,
		exact: true,
		access: VISION_ACCESS,
		name: 'Vision',
		sidebar: false // index redirect — the Cameras entry below is the visible nav item.
	},
	{
		path: 'cameras/:tab?',
		component: Cameras,
		exact: true,
		access: VISION_ACCESS,
		name: 'Cameras',
		description: 'Live view of the cameras across your spaces.',
		leftIcon: <ZezamiiIcon name='camera' />,
		sidebar: {
			access: VISION_ACCESS,
			name: 'Cameras',
			position: 0
		}
	},
	// Universal Spaces/People sub-items (Sites/Spaces, Devices, Teams, Visitors, Roles, Settings),
	// reused verbatim from Access so /vision/<path> renders the same containers and scoping.
	...VISION_SHARED_NAV_ROUTES
];

// Import the layouts for the dashboards
const AccessDashboardLayout = lazy(() => import('mainDashboard/Layout/index'));
const LockerDashboardLayout = lazy(() => import('lockerDashboard/Layout/index'));

/** Dashboard roots (Access, Lockers, Bookings, ...): each { key, path, component, routes }. */
export const DASHBOARD_ROUTES = [
	{
		key: 'access',
		path: 'access',
		component: AccessDashboardLayout,
		routes: MAIN_DASHBOARD_ROUTES
	},
	{
		key: 'lockers',
		path: 'lockers',
		component: LockerDashboardLayout,
		routes: LOCKER_DASHBOARD_ROUTES
	},
	{
		// Bookings reuses the Access dashboard shell; the shared Sidebar renders the Bookings
		// nav set (Spaces · People · Bookings) based on the mounted URL root (/bookings).
		key: 'bookings',
		path: 'bookings',
		component: AccessDashboardLayout,
		routes: BOOKINGS_DASHBOARD_ROUTES
	},
	{
		// Vision reuses the Access dashboard shell; the shared Sidebar renders the Vision nav set
		// (Cameras · Spaces · People) based on the mounted URL root (/vision).
		key: 'vision',
		path: 'vision',
		component: AccessDashboardLayout,
		routes: VISION_DASHBOARD_ROUTES
	},
	{
		key: 'dashboardChooser',
		path: 'dashboardChooser',
		component: DashboardChooser,
		routes: [] // No routes for the dashboard chooser
	},
	{
		// RETIRED cross-org Partner Portal: the standalone Partner app (partner.zezamii.com) is the partner
		// portal. Kept only as a redirect there, because the URL was handed out (bookmarks, older invite pages).
		key: 'partnerPortal',
		path: 'partner-portal',
		component: PartnerPortalRedirect,
		routes: []
	}
];
